import { useEffect, type ReactNode } from "react"
import { Navigate, useLocation } from "react-router-dom"
import { refreshAccess } from "@/services/auth.ts"
import { getToken } from "@/services/auth-storage.ts"

/**
 * Πόσο συχνά ξαναρωτάμε τον server «τι επιτρέπεται σε εμένα;».
 *
 * Πιο αραιά από τη ροή γεγονότων (5 δευτ.): η αλλαγή δικαιωμάτων είναι σπάνια
 * και δεν χρειάζεται να είναι άμεση. Μισό λεπτό είναι αρκετά γρήγορο ώστε ο
 * διαχειριστής να πει «σου το άλλαξα» και να ισχύει πριν προλάβει ο άλλος να
 * ρωτήσει «τώρα;».
 */
const ACCESS_POLL_MS = 30_000

/**
 * Φρουρός: χωρίς token → login. Με token, το /me επιβεβαιώνει ότι ισχύει
 * ακόμα — αν όχι, ο 401 handler του apiFetch καθαρίζει και ανακατευθύνει
 * μόνος του. Δεν μπλοκάρουμε το render περιμένοντας το /me: ένα stale token
 * θα κοπεί στην πρώτη κιόλας κλήση δεδομένων ούτως ή άλλως.
 *
 * ΚΡΑΤΑΕΙ ΚΑΙ ΤΑ ΔΙΚΑΙΩΜΑΤΑ ΦΡΕΣΚΑ. Πριν, η απάντηση του /me πεταγόταν: τα
 * δικαιώματα γράφονταν στο localStorage τη στιγμή της σύνδεσης και έμεναν
 * εκεί, οπότε μια αλλαγή ρόλου απαιτούσε αποσύνδεση και νέα σύνδεση.
 *
 * Όταν όντως αλλάξει κάτι, η σελίδα ΞΑΝΑΦΟΡΤΩΝΕΙ. Είναι το ειλικρινές: το
 * μενού, οι φρουροί των διαδρομών και τα κρυμμένα κουμπιά διαβάζουν όλα τα
 * δικαιώματα κατά το render, και μια μισή ενημέρωση θα άφηνε την οθόνη να
 * λέει άλλα από τον server. Γίνεται ΜΟΝΟ όταν υπάρχει πραγματική διαφορά —
 * αλλιώς θα ήταν ατέρμονη ανανέωση.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const location = useLocation()
  const token = getToken()

  useEffect(() => {
    if (!token) return

    let stopped = false

    const check = () => {
      // Κρυμμένη καρτέλα δεν ρωτά — ίδιος κανόνας με τη ροή γεγονότων.
      if (document.visibilityState === "hidden") return

      refreshAccess()
        .then((changed) => {
          if (changed && !stopped) window.location.reload()
        })
        .catch(() => {
          // Σιωπηλά: αν το token έληξε, ο 401 handler του apiFetch έχει ήδη
          // αναλάβει· αν έπεσε το δίκτυο, ξαναδοκιμάζουμε στον επόμενο κύκλο.
        })
    }

    check()
    const timer = setInterval(check, ACCESS_POLL_MS)

    // Επιστροφή στην καρτέλα: ρώτα ΤΩΡΑ. Ο υπάλληλος που γυρίζει από το
    // τηλέφωνο με τον διαχειριστή δεν πρέπει να περιμένει τον κύκλο.
    const onVisible = () => {
      if (document.visibilityState === "visible") check()
    }
    document.addEventListener("visibilitychange", onVisible)

    return () => {
      stopped = true
      clearInterval(timer)
      document.removeEventListener("visibilitychange", onVisible)
    }
  }, [token])

  if (!token) {
    return <Navigate to="/login" replace state={{ from: location }} />
  }

  return <>{children}</>
}
